/**
 * BookingFlow: main entry point and container of all steps in the booking flow.
 *
 * Individual steps are ordered and exported from steps/index.js
 *
 * Each step is provided a parameter object:
 *
 * {
 *   state, // the current state of the flow
 *   dispatch, // function to make a change to any part of the state
 *   dispatchValueAtKeyPath, // updates a specific item in state
 *   setIsStepValid, // function which modifies the state to
 *                   //   indicate the current step is fully completed
 *                    //   and the next button can be enabled
 *   moveToNextStep, // function moves to the next step
 *   moveToPreviousStep,  // function moves to the previous step
 * }
 */
import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { keys, get, set, isEqual, map, mergeWith, isArray, filter } from 'lodash'
import {
  googleAnalyticsPageView,
  trackAddServiceToCart,
  trackRemoveServiceFromCart,
  usePersistedReducer,
  segmentPageView,
  useIsDesktop,
} from '../../helpers'

// step definition and navigation functions
import {
  componentForStep,
  isValidStep,
  findProximateStepNumbers
} from '../../components/book/steps'

import {
  getAccessToken,
  useMe,
  loadMarkets,
  useAllMarketServices,
} from '../../services/GraphQlClientService'

import { mq, syncSavedAddressWithState } from '../../helpers'

import BookingFlowHeader from '../../components/book/steps/components/BookingFlowHeader'
import BookingFlowFooter from '../../components/book/steps/components/BookingFlowFooter'
import ValetWarningModal from '../../components/book/steps/components/ValetWarningModal'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { useRouter } from 'next/router'
import Head from 'next/head'

// we can log some additional data in development
const NEXT_PUBLIC_ENVIRONMENT = process.env.NEXT_PUBLIC_ENVIRONMENT
// const STRIPE_API_KEY = NEXT_PUBLIC_STRIPE_ELEMENTS_API_KEY = process.env
const STRIPE_API_KEY = process.env.NEXT_PUBLIC_STRIPE_ELEMENTS_API_KEY

const isDevelopment = NEXT_PUBLIC_ENVIRONMENT === 'development'

const BookingFlowMain = styled.main`
  max-width: 100vw;
  width: 100vw;
  flex: 1 0 auto;
  position: relative;

  .step-1 & {
    padding-bottom: 120px;

    ${mq('min', '768px')} {
      padding-bottom: 130px;
    }
  }

  .step-2 & {
    padding: 0 20px 120px;
    text-align: center;
  }

  .step-3 & {
    padding-bottom: 130px;
  }

  .step-4 & {
    > p + section {
      margin-top: 0;
    }

    ${mq('min', '768px')} {
      padding-bottom: 100px;
    }
  }

  .step-5 & {
    padding-bottom: 136px;
    
    ${mq('min', '768px')} {
      margin-top: 28px;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: 44px;
    }
  }

  .step-6 & {
    padding-bottom: 20px;

    ${mq('min', '768px')} {
      padding-bottom: 100px;
    }
  }
`

export const getStaticProps = async () => {
  const revalidationTime = process.env.NEXT_PUBLIC_REVALIDATE_MINUTES
  const markets = await loadMarkets()

  return {
    props: {
      markets,
    },
    revalidate: revalidationTime * 60
  }
}

export default function Book({ history, location, markets, code, appDispatch }) {
  // preload some stuff
  const me = useMe()
  // const search = useSearchParams()

  // console.log(search)
  // console.log(search.get('resume'))

  const marketServices = useAllMarketServices()

  // load stripe
  const stripePromise = loadStripe(STRIPE_API_KEY)

  const isDesktop = useIsDesktop()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatchValueAtKeyPath('isDesktop', isDesktop)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop])

  // defaults for state go here
  let initialState = {
    // NOTE: do not set a default for any state synced from querystring or it will not sync
    isModalOpen: false,
    isDesktop,
    address: {
      id: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      apartment: '',
      notes: '',
      type: '',
      pets: '',
      latitude: 0,
      longitude: 0
    },
    step: 1,
    serviceRequests: [],
    referrer: (global?.document && global?.document?.referrer)
      ? new URL(global?.document?.referrer)
      : (isDevelopment
        ? 'https://www-dev.glamsquad.com/'
        : 'https://www.glamsquad.com/'
      )
  }

  // this useReducer call sets up the state of the entire booking flow
  // and the dispatch function to modify it
  const { state, reducerDispatch } = usePersistedReducer(
    (current, updated = {}) => {
      // only update the state if something has changed to avoid re-render loops
      const isChanged =
        !keys(current).length ||
        !!keys(updated).find(key => updated[key] !== current[key])

      if (!isChanged) {
        return current
      }

      const merged = mergeWith({}, current, updated, (objValue, srcValue) => {
        // if array, overwrite w/ new src value instead of merging
        if (Array.isArray(srcValue)) {
          return srcValue
        }
      })

      // merge in updated state if changed
      return merged
    },
    initialState,
    'book'
  )

  // this just wraps the reducer's dispatch, but returns null so
  // components can return from a dispatch
  const dispatch = (...args) => {
    reducerDispatch(...args)
    return null
  }

  // dispatches a change to state if value at keyPath has changed
  const dispatchValueAtKeyPath = (keyPath, value) => {
    // do not dispatch if unchanged
    if (isEqual(get(state, keyPath), value)) {
      return null
    }

    return dispatch(set({ ...state }, keyPath, value))
  }

  const { query } = useRouter()

  const marketShortName = query?.mkt || undefined
  const serviceName = query?.svc || undefined
  const serviceType = query?.stp || undefined

  // check if a market has been provided and select if found
  useEffect(() => {
    if (!(markets && marketShortName)) {
      return
    }

    const market = markets.find(
      ({ shortName }) =>
        shortName && shortName.toLowerCase() === marketShortName.toLowerCase()
    )
    if (!market) {
      return
    }

    if (!marketServices) {
      return
    }

    const marketService = marketServices
      .filter(({ marketId }) => marketId === market.id)
      .find(
        ({ service: { name } }) =>
          serviceName && serviceName.toLowerCase() === name.toLowerCase()
      )

    dispatch({
      step: 2,
      allowAddressSelect: false,
      market: market,
      displayedMarketService: marketService,
      isMarketServiceModalOpen: !!marketServices,
      scrollToServiceType: serviceType,
      address: {
        id: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        apartment: '',
        notes: '',
        type: '',
        pets: '',
        latitude: 0,
        longitude: 0
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketShortName, JSON.stringify(markets), JSON.stringify(marketServices), serviceName, serviceType])

  //Once user logs in, if the address saved matches current, update to saved.
  syncSavedAddressWithState(me, state, dispatch)

  // valet modal
  const [valetModalOpen, setValetModalOpen] = useState(false)
  const [valetModalTitle, setValetModalTitle] = useState(undefined)
  const [valetModalExplanation, setValetModalExplanation] = useState(undefined)
  const closeValetModal = () => {
    setValetModalOpen(false)
    setValetModalTitle(undefined)
    setValetModalExplanation(undefined)
  }

  // extract some step info from state
  let { step, isStepValid, nextButtonLabel = 'Select Your Service', isModalOpen, selectedAppointment } = state

  useEffect(() => {
    // log each change in step as a page view to google analytics
    // two pageviews for each step in order to distinguish in GA
    const pageviewForStep = [
      '',
      ['/book/v3/'],
      ['/book/v3/services'],
      ['/book/v3/availability-search'],
      ['/book/v3/sign-in'],
      ['/book/v3/payment'],
      ['/book/v3/confirmation', '/book/confirmation']
    ]

    const pageview = pageviewForStep[step]
    // convert to array of pageviews unless it is already
    const pageviews = isArray(pageview) ? pageview : [pageview]

    window.history.replaceState(undefined, undefined, `/book/${step}`)

    pageviews.forEach(pageview => {
      googleAnalyticsPageView(pageview)
      segmentPageView(pageview)
    })
  }, [step])

  if (!step) {
    return null
  }

  // include authentication status in w/ the state just to make it available
  state.isAuthenticated = !!getAccessToken()

  const [nextStep, previousStep] = findProximateStepNumbers(step, state)

  // enabled if step w/in range and current state passes current step's validation
  const nextButtonEnabled = isValidStep(nextStep) && isStepValid

  // enabled if step w/in range
  const previousButtonEnabled = isValidStep(previousStep)

  // fns to change current step
  const moveToNextStep = () => {
    if (step === 3 && (state?.address?.type === 'Hotel' || selectedAppointment?.additionalFees?.length)) {
      setValetModalOpen(true)
      setValetModalTitle(selectedAppointment?.additionalFees?.[0]?.description)
      setValetModalExplanation(selectedAppointment?.additionalFees?.[0]?.explanation)
    } else {
      dispatchValueAtKeyPath('step', nextStep)
      window.scrollTo(0, 0)
    }
  }

  const moveToPreviousStep = () => {
    dispatchValueAtKeyPath('step', previousStep)
    window.scrollTo(0, 0)
  }

  isDevelopment &&
    console.log(
      JSON.stringify(
        {
          previousStep: previousStep,
          currentStep: step,
          nextStep: nextStep,
          isAuthenticated: state.isAuthenticated,
          market: get(state, 'market.shortName', 'NOT SET'),
          address: get(state, 'address.street', 'NOT SET')
        },
        null,
        2
      )
    )

  let next

  // function passed down to steps
  const setIsStepValid = (
    isStepValid,
    nextButtonLabel = 'Next Step',
    stepNext
  ) => {
    next = stepNext || moveToNextStep

    if (isStepValid === state.isStepValid
      && nextButtonLabel === state.nextButtonLabel) {
      return
    }
    dispatch({ isStepValid, nextButtonLabel })
  }

  // open and close modal
  const openModal = () => {
    dispatchValueAtKeyPath('isModalOpen', true)
    return false
  }

  const closeModal = () => {
    dispatchValueAtKeyPath('isModalOpen', false)
    return false
  }

  const { serviceRequests } = state

  // dispatches state update to select a market service
  const selectMarketService = ({
    marketService,
    addOnMarketServices = [],
    instances = 1,
    backToBack = false
  }) => {
    const {
      service: { id: serviceId }
    } = marketService

    const serviceRequest = {
      serviceId,
      instances,
      backToBack,
      addOnServiceIds: map(addOnMarketServices, 'service.id')
    }

    dispatch({
      // filter out same service to replace if present and append new request
      serviceRequests: serviceRequests
        .filter(sr => sr.serviceId !== serviceId)
        .concat([serviceRequest]),
      isModalOpen: false
    })

    trackAddServiceToCart(state.market, marketService, instances, addOnMarketServices)
  }

  // dispatches state update to deselect a market service
  const deselectMarketService = serviceId => {
    const removedService = filter(marketServices, ms => ms.marketId === state.market.id)
      .find(ms => ms.service.id === serviceId)

    trackRemoveServiceFromCart(state.market, removedService, serviceRequests.find(sr => sr.serviceId === serviceId))

    dispatchValueAtKeyPath(
      'serviceRequests',
      serviceRequests.filter(sr => sr.serviceId !== serviceId)
    )
  }

  // get the current step component
  const CurrentStep = componentForStep(step)

  return (
    <Elements stripe={stripePromise}>
      <Head>
        <title>Book | Glamsquad</title>
      </Head>
      <div className={`booking-flow step-${step}`}>
        <BookingFlowHeader
          {...{
            step,
            prev: previousButtonEnabled && moveToPreviousStep,
            referrer: state.referrer,
            history
          }}
        />
        <BookingFlowMain>
          <CurrentStep
            {...{
              state,
              dispatch,
              dispatchValueAtKeyPath,
              setIsStepValid,
              moveToNextStep,
              moveToPreviousStep,
              nextStep,
              previousStep,
              isModalOpen,
              openModal,
              closeModal,
              selectMarketService,
              deselectMarketService,
              me,
              next: () => next(),
              nextButtonEnabled,
              nextButtonLabel,
              step,
              history,
              location,
              code,
              appDispatch
            }}
          />
        </BookingFlowMain>
        <BookingFlowFooter
          {...{
            state,
            next: (...args) => next(...args), // call whatever next function we've been provided
            nextButtonEnabled,
            nextButtonLabel,
            deselectMarketService,
            dispatchValueAtKeyPath
          }}
        />
        <ValetWarningModal
          isOpen={valetModalOpen}
          close={closeValetModal}
          isDesktop={isDesktop}
          nextStep={nextStep}
          dispatch={dispatchValueAtKeyPath}
          title={valetModalTitle}
          explanation={valetModalExplanation}
        />
      </div>
    </Elements>
  )
}
